import React, { useEffect, useState } from 'react';
import { CircularProgress } from '@mui/material';
import {
  PersonOutline as AgentIcon,
  Refresh as RefreshIcon,
  DateRange as DateIcon,
  Search as SearchIcon,
  ConfirmationNumber as TicketIcon,
  CheckCircle as ResolvedIcon,
  AccessTime as FRTIcon,
  Timer as ARTIcon,
  Assignment as AssignedIcon,
  TrendingUp as EscalatedIcon,
  Timeline as TimelineIcon,
  Category as CategoryIcon,
  Email as EmailIcon,
  Badge as BadgeIcon,
  CalendarToday as CalendarIcon,
  Visibility as ViewIcon,
  FolderOpen as OpenIcon,
  DoneAll as DoneIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import adminTicketService from '../../services/adminTicketService';
import './TicketStyles.css';

interface AgentReport {
  adminId?: string;
  name?: string;
  email?: string;
  total?: number;
  resolved?: number;
  assignedCount?: number;
  reassigned?: number;
  escalated?: number;
  avgFRT?: number;
  avgART?: number;
}

interface AgentDetail {
  admin?: {
    name: string;
    email: string;
    isActive: boolean;
    isSuperAdmin: boolean;
    isAdmin: boolean;
    createdAt: string;
  };
  timeSeries: { _id: string; total: number; resolved: number }[];
  categoryBreakdown: { _id: string; total: number }[];
}

function formatDuration(ms?: number): string {
  if (!ms || ms < 0) return '-';
  const sec = Math.round(ms / 1000);
  const day = Math.floor(sec / 86400);
  const hour = Math.floor((sec % 86400) / 3600);
  const minute = Math.floor((sec % 3600) / 60);
  const second = sec % 60;
  if (day >= 1) return `${day} روز`;
  if (hour >= 1) return `${hour} ساعت`;
  if (minute >= 1) return `${minute} دقیقه`;
  return `${second} ثانیه`;
}

const AgentReportsPage: React.FC = () => {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [reports, setReports] = useState<AgentReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedAdminEmail, setSelectedAdminEmail] = useState<string>('');
  const [detail, setDetail] = useState<AgentDetail | null>(null);
  const [interval, setInterval] = useState<'day' | 'week' | 'month'>('day');
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminTicketService.getAgentReports({ dateFrom, dateTo });
      setReports(data);
    } catch (error) {
      console.error('Error loading agent reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDetail = async (email: string) => {
    if (!email) return;
    setDetailLoading(true);
    try {
      const data = await adminTicketService.getAgentReportDetail(email, { dateFrom, dateTo, interval });
      setDetail(data);
    } catch (error) {
      console.error('Error loading agent detail:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const formatNumber = (num?: number) => {
    if (num === undefined || num === null) return '-';
    return new Intl.NumberFormat('fa-IR').format(num);
  };

  const getInitials = (name?: string) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').substring(0, 2);
  };

  return (
    <>
      <Helmet>
        <title>گزارش اپراتورها</title>
      </Helmet>

      <div className="ticket-page">
        {/* Header */}
        <div className="ticket-header">
          <div className="ticket-header-title">
            <h1>
              <AgentIcon />
              گزارش اپراتورها
            </h1>
            <span className="ticket-header-subtitle">
              عملکرد فردی اپراتورهای پشتیبانی در مدیریت تیکت‌ها
            </span>
          </div>

          <div className="header-actions">
            <button className="header-btn" onClick={load} disabled={loading}>
              <RefreshIcon style={{ fontSize: 18 }} />
              بروزرسانی
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="filters-section">
          <div className="filters-header">
            <span className="filters-title">
              <DateIcon />
              فیلتر و جستجو
            </span>
          </div>
          <div className="filters-grid" style={{ gridTemplateColumns: 'repeat(5, 1fr)' }}>
            <div className="filter-group">
              <label className="filter-label">از تاریخ</label>
              <input
                type="date"
                className="filter-input"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label className="filter-label">تا تاریخ</label>
              <input
                type="date"
                className="filter-input"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label className="filter-label">بازه گزارش</label>
              <select
                className="filter-select"
                value={interval}
                onChange={(e) => setInterval(e.target.value as 'day' | 'week' | 'month')}
              >
                <option value="day">روزانه</option>
                <option value="week">هفتگی</option>
                <option value="month">ماهانه</option>
              </select>
            </div>
            <div className="filter-group">
              <label className="filter-label">ایمیل اپراتور</label>
              <input
                type="email"
                className="filter-input"
                value={selectedAdminEmail}
                onChange={(e) => setSelectedAdminEmail(e.target.value)}
                placeholder="ایمیل را وارد کنید"
              />
            </div>
            <div className="filter-group" style={{ alignSelf: 'flex-end' }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="filter-btn primary" onClick={load} disabled={loading}>
                  اعمال
                </button>
                <button
                  className="filter-btn secondary"
                  onClick={() => loadDetail(selectedAdminEmail)}
                  disabled={!selectedAdminEmail || detailLoading}
                >
                  <SearchIcon style={{ fontSize: 16 }} />
                  جزئیات
                </button>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="loading-overlay">
            <CircularProgress size={40} />
            <p>در حال بارگذاری...</p>
          </div>
        ) : reports.length === 0 ? (
          <div className="empty-state">
            <AgentIcon />
            <h3>گزارشی یافت نشد</h3>
            <p>در بازه زمانی انتخابی گزارشی ثبت نشده است</p>
          </div>
        ) : (
          <>
            {/* Agent Cards Grid */}
            <div className="cards-grid">
              {reports.map((report, index) => (
                <div
                  key={report.adminId || report.email || index}
                  className="item-card"
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    if (report.email) {
                      setSelectedAdminEmail(report.email);
                      loadDetail(report.email);
                    }
                  }}
                >
                  <div className="item-card-header" style={{ marginBottom: 16 }}>
                    <div className="agent-row">
                      <div className="agent-avatar">
                        {getInitials(report.name)}
                      </div>
                      <div className="agent-info">
                        <span className="agent-name">{report.name || 'بدون مسئول'}</span>
                        {report.email && <span className="agent-email">{report.email}</span>}
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <TicketIcon style={{ fontSize: 18, color: 'var(--color-primary)' }} />
                      <div>
                        <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--color-text-primary)' }}>
                          {formatNumber(report.total)}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>کل تیکت‌ها</div>
                      </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <ResolvedIcon style={{ fontSize: 18, color: 'var(--color-success)' }} />
                      <div>
                        <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--color-text-primary)' }}>
                          {formatNumber(report.resolved)}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>حل شده</div>
                      </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <AssignedIcon style={{ fontSize: 18, color: 'var(--color-primary)' }} />
                      <div>
                        <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--color-text-primary)' }}>
                          {formatNumber(report.assignedCount)}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>واگذاری</div>
                      </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <EscalatedIcon style={{ fontSize: 18, color: 'var(--color-warning-dark)' }} />
                      <div>
                        <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--color-text-primary)' }}>
                          {formatNumber(report.reassigned ?? report.escalated)}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>ارجاع</div>
                      </div>
                    </div>
                  </div>

                  <div className="item-card-meta" style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--color-divider)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <FRTIcon style={{ fontSize: 14 }} />
                      <span>FRT: {formatDuration(report.avgFRT)}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <ARTIcon style={{ fontSize: 14 }} />
                      <span>ART: {formatDuration(report.avgART)}</span>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  {report.adminId && (
                    <div style={{
                      display: 'flex',
                      gap: 8,
                      marginTop: 16,
                      paddingTop: 16,
                      borderTop: '1px solid var(--color-divider)'
                    }}>
                      <button
                        className="header-btn"
                        style={{ flex: 1, justifyContent: 'center', padding: '8px 12px', fontSize: 12 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/tickets/list?assignedTo=${report.adminId}`);
                        }}
                        title="مشاهده همه تیکت‌ها"
                      >
                        <ViewIcon style={{ fontSize: 16 }} />
                        همه
                      </button>
                      <button
                        className="header-btn"
                        style={{ flex: 1, justifyContent: 'center', padding: '8px 12px', fontSize: 12, borderColor: 'var(--color-success)', color: 'var(--color-success)' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/tickets/list?assignedTo=${report.adminId}&status=RESOLVED`);
                        }}
                        title="تیکت‌های حل شده"
                      >
                        <DoneIcon style={{ fontSize: 16 }} />
                        حل شده
                      </button>
                      <button
                        className="header-btn"
                        style={{ flex: 1, justifyContent: 'center', padding: '8px 12px', fontSize: 12, borderColor: 'var(--color-warning-dark)', color: 'var(--color-warning-dark)' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/tickets/list?assignedTo=${report.adminId}&status=OPEN,IN_PROGRESS`);
                        }}
                        title="تیکت‌های در انتظار"
                      >
                        <OpenIcon style={{ fontSize: 16 }} />
                        در انتظار
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Detail Section */}
            {detail && (
              <div style={{ marginTop: 24 }}>
                <h2 style={{
                  fontSize: 16,
                  fontWeight: 600,
                  color: 'var(--color-text-primary)',
                  marginBottom: 16,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}>
                  <AgentIcon style={{ color: 'var(--color-primary)' }} />
                  جزئیات اپراتور
                </h2>

                {detailLoading ? (
                  <div className="loading-overlay" style={{ padding: 40 }}>
                    <CircularProgress size={32} />
                  </div>
                ) : (
                  <div className="cards-grid">
                    {/* Admin Info Card */}
                    {detail.admin && (
                      <div className="item-card" style={{ gridColumn: 'span 3' }}>
                        <div className="item-card-header">
                          <h3 className="item-card-title">
                            <BadgeIcon style={{ fontSize: 18, marginLeft: 8, color: 'var(--color-primary)' }} />
                            اطلاعات اپراتور
                          </h3>
                        </div>
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(5, 1fr)',
                          gap: 16,
                          marginTop: 16
                        }}>
                          <div>
                            <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 4 }}>نام</div>
                            <div style={{ fontWeight: 500, color: 'var(--color-text-primary)' }}>{detail.admin.name || '-'}</div>
                          </div>
                          <div>
                            <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 4 }}>
                              <EmailIcon style={{ fontSize: 12, marginLeft: 4 }} />
                              ایمیل
                            </div>
                            <div style={{ fontWeight: 500, color: 'var(--color-text-primary)', fontSize: 13 }}>{detail.admin.email || '-'}</div>
                          </div>
                          <div>
                            <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 4 }}>وضعیت</div>
                            <span className={`status-badge ${detail.admin.isActive ? 'resolved' : 'closed'}`}>
                              {detail.admin.isActive ? 'فعال' : 'غیرفعال'}
                            </span>
                          </div>
                          <div>
                            <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 4 }}>نقش</div>
                            <span className="priority-badge medium">
                              {detail.admin.isSuperAdmin ? 'سوپرادمین' : detail.admin.isAdmin ? 'ادمین' : 'نامشخص'}
                            </span>
                          </div>
                          <div>
                            <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 4 }}>
                              <CalendarIcon style={{ fontSize: 12, marginLeft: 4 }} />
                              تاریخ عضویت
                            </div>
                            <div style={{ fontWeight: 500, color: 'var(--color-text-primary)' }}>
                              {detail.admin.createdAt ? new Date(detail.admin.createdAt).toLocaleDateString('fa-IR') : '-'}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Time Series Card */}
                    <div className="item-card">
                      <div className="item-card-header">
                        <h3 className="item-card-title">
                          <TimelineIcon style={{ fontSize: 18, marginLeft: 8, color: 'var(--color-primary)' }} />
                          روند زمانی
                        </h3>
                      </div>
                      <div style={{ marginTop: 12, maxHeight: 200, overflowY: 'auto' }}>
                        {detail.timeSeries.length === 0 ? (
                          <p style={{ color: 'var(--color-text-disabled)', fontSize: 13 }}>داده‌ای موجود نیست</p>
                        ) : (
                          detail.timeSeries.map((item) => (
                            <div
                              key={item._id}
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '8px 0',
                                borderBottom: '1px solid var(--color-divider)'
                              }}
                            >
                              <span style={{ color: 'var(--color-text-secondary)', fontSize: 13 }}>{item._id}</span>
                              <div style={{ display: 'flex', gap: 12 }}>
                                <span style={{ color: 'var(--color-primary)', fontSize: 13, fontWeight: 500 }}>
                                  کل: {formatNumber(item.total)}
                                </span>
                                <span style={{ color: 'var(--color-success)', fontSize: 13, fontWeight: 500 }}>
                                  حل: {formatNumber(item.resolved)}
                                </span>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    {/* Category Breakdown Card */}
                    <div className="item-card">
                      <div className="item-card-header">
                        <h3 className="item-card-title">
                          <CategoryIcon style={{ fontSize: 18, marginLeft: 8, color: 'var(--color-warning-dark)' }} />
                          تفکیک دسته‌بندی
                        </h3>
                      </div>
                      <div style={{ marginTop: 12, maxHeight: 200, overflowY: 'auto' }}>
                        {detail.categoryBreakdown.length === 0 ? (
                          <p style={{ color: 'var(--color-text-disabled)', fontSize: 13 }}>داده‌ای موجود نیست</p>
                        ) : (
                          detail.categoryBreakdown.map((item) => (
                            <div
                              key={item._id}
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '8px 0',
                                borderBottom: '1px solid var(--color-divider)'
                              }}
                            >
                              <span style={{ color: 'var(--color-text-secondary)', fontSize: 13 }}>{item._id || 'بدون دسته'}</span>
                              <span
                                className="status-badge"
                                style={{ background: 'var(--color-warning-bg)', color: 'var(--color-warning-dark)' }}
                              >
                                {formatNumber(item.total)}
                              </span>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default AgentReportsPage;
